import { useMemo, useState } from 'react'
import { useStore } from '@/store'
import DetailTable from './DetailTable'
import { worldHopsToDetailRows } from './detailRows'
import type { WorldFlowHop } from '@/analysis/worldFlowTypes'

type WorldTab = 'studio' | 'cdn_edge' | 'city'

const TABS: { id: WorldTab; label: string }[] = [
  { id: 'studio', label: 'Studio' },
  { id: 'cdn_edge', label: 'Delivery' },
  { id: 'city', label: 'Client' },
]

function pillStyle(active: boolean): React.CSSProperties {
  return {
    fontSize: 11,
    fontWeight: 600,
    padding: '4px 10px',
    borderRadius: 6,
    border: `0.5px solid ${active ? 'var(--info-bd)' : 'var(--bd-0)'}`,
    background: active ? 'var(--info-bg)' : 'transparent',
    color: active ? 'var(--info)' : 'var(--t-0)',
    cursor: 'pointer',
  }
}

/**
 * World details: one hop-type table at a time (Studio / Delivery / Client tabs).
 * Metrics are columns via DetailTable; row click matches map drill gestures.
 */
export default function WorldDetailTable() {
  const flow = useStore((s) => s.worldFlowResult)
  const clientGrain = useStore((s) => s.worldClientGrain)
  const clearGeoFilters = useStore((s) => s.clearGeoFilters)
  const descendToStreams = useStore((s) => s.descendToStreams)
  const setClientGeoFilter = useStore((s) => s.setClientGeoFilter)
  const setFilterOnly = useStore((s) => s.setFilterOnly)
  const setDimension = useStore((s) => s.setDimension)
  const setView = useStore((s) => s.setView)
  const [tab, setTab] = useState<WorldTab>('studio')

  const counts = useMemo(() => {
    const hops = flow?.hops ?? []
    return {
      studio: hops.filter((h) => h.type === 'studio').length,
      cdn_edge: hops.filter((h) => h.type === 'cdn_edge').length,
      city: hops.filter((h) => h.type === 'city').length,
    }
  }, [flow])

  const rows = useMemo(() => {
    const hops = (flow?.hops ?? []).filter((h) => h.type === tab)
    const activate = (h: WorldFlowHop) => {
      if (h.type === 'studio') {
        clearGeoFilters()
        descendToStreams('studio', h.value)
        setView('hex')
        return
      }
      if (h.type === 'cdn_edge') {
        setFilterOnly('cdnEdge', h.value)
        setDimension('stream')
        setView('hex')
        return
      }
      if (h.type === 'city') {
        const parts = h.value.split('|')
        if (clientGrain === 'continent') {
          setClientGeoFilter({ grain: 'continent', continent: h.value })
        } else if (
          clientGrain === 'country' ||
          (parts.length !== 2 && (h.country || /^[A-Za-z]{2}$/.test(h.value)))
        ) {
          setClientGeoFilter({ grain: 'country', country: (h.country ?? h.value).toUpperCase() })
        } else if (parts.length === 2) {
          setClientGeoFilter({ grain: 'city', city: parts[0]!, country: parts[1]! })
        } else {
          setClientGeoFilter({ grain: 'city', city: h.value })
        }
        setDimension('stream')
        setView('hex')
      }
    }
    // Studio rows keep location as sub; other tabs leave sub null (tab names the hop kind).
    return worldHopsToDetailRows(hops, activate).map((r) =>
      tab === 'studio' ? r : { ...r, sub: null },
    )
  }, [
    flow,
    tab,
    clientGrain,
    clearGeoFilters,
    descendToStreams,
    setClientGeoFilter,
    setFilterOnly,
    setDimension,
    setView,
  ])

  const empty =
    tab === 'studio'
      ? 'No studio hops for the current filters.'
      : tab === 'cdn_edge'
        ? 'No delivery hops for the current filters.'
        : 'No client hops for the current filters.'

  return (
    <div className="panel" style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '8px 12px',
          borderBottom: '0.5px solid var(--bd-0)',
          flexShrink: 0,
        }}
      >
        <span style={{ fontSize: 10, color: 'var(--t-2)', fontWeight: 600, marginRight: 4 }}>
          World details
        </span>
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            style={pillStyle(tab === t.id)}
            onClick={() => setTab(t.id)}
            aria-pressed={tab === t.id}
          >
            {t.label}
            <span style={{ opacity: 0.75, fontWeight: 500, marginLeft: 6 }}>{counts[t.id]}</span>
          </button>
        ))}
      </div>
      <div style={{ flex: 1, minHeight: 0 }}>
        <DetailTable rows={rows} emptyText={empty} />
      </div>
    </div>
  )
}
